import { useMutation } from '@apollo/client/react';
import { Grid, TextField } from '@mui/material';
import { map } from 'lodash';
import { useContext, useState } from 'react';

import OverwatchModal from '@/components/OverwatchModal';
import { ReportContext } from '@/Contexts/ReportContext';
import {
  type DeliveryAndCollectionRegisterItem,
  type DeliveryAndCollectionItemState,
  UpdateDeliveryAndCollectionItemDocument,
  GetDeliveryItemStatesDocument,
} from '@/graphql/generated/graphql';
import { displayNotification } from '@/utils/notifications';

interface EditDeliveryModalProps {
  handleClose: () => void;
  deliveryItem: DeliveryAndCollectionRegisterItem;
  itemState: DeliveryAndCollectionItemState;
}

const EditDeliveryModal: React.FC<EditDeliveryModalProps> = ({ handleClose, deliveryItem, itemState }) => {
  const { selectedReport } = useContext(ReportContext);
  const [formData, setFormData] = useState({
    quantity: deliveryItem.quantity,
    supplier: itemState.supplier,
    delivererName: itemState.delivererName || '',
    receiverName: itemState.receiverName || '',
    notes: itemState.notes || '',
  });

  const [updateDeliveryItem, { loading }] = useMutation(UpdateDeliveryAndCollectionItemDocument, {
    onCompleted: () => {
      displayNotification({
        text: 'Delivery item updated successfully',
        type: 'success',
      });
      handleClose();
    },
    onError: (error) => {
      displayNotification({ text: error.message, type: 'error' });
    },
  });

  const handleSubmit = () => {
    const variables = {
      id: deliveryItem.id,
      stateId: itemState.id,
      ...formData,
    };

    updateDeliveryItem({
      variables,
      update(cache, { data }) {
        const updatedState = data?.updateDeliveryAndCollectionItem;
        const reportId = selectedReport?.id;

        if (!reportId || !updatedState) return;

        const existingItems =
          cache.readQuery({
            query: GetDeliveryItemStatesDocument,
            variables: { reportId },
          })?.getDeliveryItemStates ?? [];

        cache.writeQuery({
          query: GetDeliveryItemStatesDocument,
          variables: { reportId },
          data: {
            getDeliveryItemStates: map(existingItems, (item) => (item.id === updatedState.id ? updatedState : item)),
          },
        });
      },
    });
  };

  const hasChanges = () => {
    return (
      formData.quantity !== deliveryItem.quantity ||
      formData.supplier !== itemState.supplier ||
      formData.notes !== itemState.notes
    );
  };

  return (
    <OverwatchModal
      title="Edit Delivery Item"
      handleClose={handleClose}
      primaryButtonText="Save Changes"
      primaryButtonAction={handleSubmit}
      disablePrimary={!hasChanges() || loading}
      secondaryButtonText="Cancel"
      secondaryButtonAction={handleClose}
      size="sm"
    >
      <Grid container spacing={2} padding={2} direction="column">
        <Grid size={12} mt={2}>
          <TextField
            fullWidth
            label="Quantity"
            value={formData.quantity}
            onChange={(e) => setFormData((prev) => ({ ...prev, quantity: e.target.value }))}
          />
        </Grid>
        <Grid size={12} mt={2}>
          <TextField
            fullWidth
            label="Supplier"
            value={formData.supplier}
            onChange={(e) => setFormData((prev) => ({ ...prev, supplier: e.target.value }))}
          />
        </Grid>
        <Grid size={12} mt={2}>
          <TextField
            fullWidth
            label="Deliverer"
            value={formData.delivererName}
            onChange={(e) => setFormData((prev) => ({ ...prev, delivererName: e.target.value }))}
          />
        </Grid>
        <Grid size={12} mt={2}>
          <TextField
            fullWidth
            label="Receiver"
            value={formData.receiverName}
            onChange={(e) => setFormData((prev) => ({ ...prev, receiverName: e.target.value }))}
          />
        </Grid>
        <Grid size={12} mt={2}>
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Notes"
            value={formData.notes}
            onChange={(e) => setFormData((prev) => ({ ...prev, notes: e.target.value }))}
          />
        </Grid>
      </Grid>
    </OverwatchModal>
  );
};

export default EditDeliveryModal;
